import React, { useCallback, useEffect, useState } from "react";
import { CaretRightOutlined } from "@ant-design/icons";
import { Col, Collapse, Form, message, Pagination, Row, Space } from "antd";
import axios from "axios";

import PageHeader from "../Layout/PageHeader";
import { usePermissions } from "../App/PermissionContext";
import BonusCampaignList from "./BonusCampaignList";
import BonusCampaignForm from "./BonusCampaignForm";
import { PERMISSIONS } from "../../constants/roles";
import {
  CAMPAIGN_TRIGGERS,
  VOUCHER_REWARD_MODES,
} from "../../constants/campaign";
import BonusCampaignFilters from "./BonusCampaignFilters";
import { buildPackageAmounts } from "./packageAmounts";

const BonusCampaign = () => {
  const { permissions } = usePermissions();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState({
    create: false,
    index: false,
    // Campaign ids with an export request in flight, so each row tracks its own spinner.
    export: new Set(),
  });
  const [campaigns, setCampaigns] = useState([]);
  const [pagination, setPagination] = useState({ currentPage: 1, limit: 20 });
  const [filters, setFilters] = useState({});
  const [welcomePackageLabels, setWelcomePackageLabels] = useState({});
  const [vouchers, setVouchers] = useState([]);

  const canCreate = permissions?.includes(PERMISSIONS.CREATE_CAMPAIGN);
  const canUpdate = permissions?.includes(PERMISSIONS.UPDATE_CAMPAIGN);
  const canExport = permissions?.includes(
    PERMISSIONS.EXPORT_CAMPAIGN_VOUCHER_DISPATCH,
  );

  const fetchWelcomePackageLabels = useCallback(async () => {
    try {
      const res = await axios.get("/api/welcome-packages", {
        params: { isActive: true, limit: 100 },
      });

      setWelcomePackageLabels(
        (res.data.data || []).reduce(
          (labels, welcomePackage) => ({
            ...labels,
            [welcomePackage.amount]: welcomePackage.name?.en,
          }),
          {}
        )
      );
    } catch (error) {
      // Roles without welcome package access still see plain "RM {amount}" labels.
      setWelcomePackageLabels({});
    }
  }, []);

  // Every voucher, not only active ones, so a campaign still shows the name of a voucher that has since
  // been switched off. The form only offers active, unexpired vouchers for new picks.
  const fetchVouchers = useCallback(async () => {
    try {
      const res = await axios.get("/api/vouchers", { params: { limit: 1000 } });
      setVouchers(res.data.data || []);
    } catch (error) {
      setVouchers([]);

      // Roles without VOUCHER_PAGE get an empty picker and saved vouchers show as unknown. Any other
      // failure is surfaced, so the picker isn't silently empty.
      if (error.response?.status !== 403) {
        message.error(error.response?.data?.message || error.message);
      }
    }
  }, []);

  const fetchCampaigns = useCallback(async () => {
    setLoading((prev) => ({ ...prev, index: true }));

    try {
      const params = {
        limit: pagination.limit,
        page: pagination.currentPage,
        ...(filters.search ? { search: filters.search } : {}),
        ...(filters.searchField ? { searchField: filters.searchField } : {}),
        ...(filters.language ? { language: filters.language } : {}),
        ...(filters.isActive !== undefined &&
        filters.isActive !== null &&
        filters.isActive !== ""
          ? { isActive: filters.isActive }
          : {}),
        ...(filters.dateRange?.[0]
          ? { startDate: filters.dateRange[0].startOf("day").toISOString() }
          : {}),
        ...(filters.dateRange?.[1]
          ? { endDate: filters.dateRange[1].endOf("day").toISOString() }
          : {}),
        ...(filters.category && { category: filters.category }),
      };

      const res = await axios.get("/api/campaigns", { params });

      setCampaigns(res.data.data || []);
      setPagination((prev) => ({ ...prev, ...res.data.pagination }));
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setLoading((prev) => ({ ...prev, index: false }));
    }
  }, [pagination.currentPage, pagination.limit, filters]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  useEffect(() => {
    fetchWelcomePackageLabels();
  }, [fetchWelcomePackageLabels]);

  // Only the create and edit forms use the voucher list.
  useEffect(() => {
    if (canCreate || canUpdate) fetchVouchers();
  }, [canCreate, canUpdate, fetchVouchers]);

  const handleFiltersChange = (newFilters) => {
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
    }));
    setFilters(newFilters);
  };

  const normalizeCampaignDetails = (details = []) =>
    (details || [])
      .filter((item) => Number(item?.bonus) > 0)
      .map((item) => ({
        packageAmount: item.packageAmount,
        bonus: item.bonus,
        ...(item.limit !== undefined && item.limit !== null && item.limit !== ""
          ? { limit: item.limit }
          : {}),
      }));

  // Sent on update too, so clearing every voucher or the minimum actually clears them on the API.
  // The API merges an update with the stored campaign and rejects one holding both a voucher list and
  // packages, so saving packages also sends an empty list. Only an explicit packages mode clears the
  // list: anything else keeps the list branch, which leaves stored packages alone.
  const buildVoucherRewardPayload = (values) => {
    const trigger = values.trigger || CAMPAIGN_TRIGGERS.TOPUP;

    if (values.voucherRewardMode === VOUCHER_REWARD_MODES.PACKAGES) {
      return {
        trigger,
        minTopupAmount: null,
        vouchers: [],
        voucherPackages: (values.voucherPackages || []).map((row) => ({
          topupAmount: row.topupAmount,
          voucherId: row.voucherId,
          quantity: row.quantity,
          ...(row.welcomeVoucherId && { welcomeVoucherId: row.welcomeVoucherId }),
        })),
      };
    }

    return {
      trigger,
      minTopupAmount: values.minTopupAmount ?? null,
      vouchers: values.vouchers || [],
    };
  };

  const createHandler = async (values) => {
    setLoading((prev) => ({ ...prev, create: true }));
    try {

      const payload = {
        name: values.name,
        description: values.description || "",
        category: values.category,
        isFirstTopupOnly: values.isFirstTopupOnly,
        startDate: values.campaignPeriod?.[0]
          ? values.campaignPeriod[0].startOf("day").toISOString()
          : "",
        endDate: values.campaignPeriod?.[1]
          ? values.campaignPeriod[1].endOf("day").toISOString()
          : "",
        campaignDetails: normalizeCampaignDetails(values.campaignDetails),
        badge: JSON.stringify({
          en: values.badge_en || "",
          zh: values.badge_zh || "",
          ms: values.badge_ms || "",
        }),
        status: values.status || "",
        ...buildVoucherRewardPayload(values),
      };

      await axios.post("/api/campaigns", payload);

      form.resetFields();
      message.success(
        "Campaign created. It is inactive by default — activate it from the list below.",
      );
      fetchCampaigns();
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setLoading((prev) => ({ ...prev, create: false }));
    }
  };

  const toggleHandler = async (id, isActive) => {
    try {
      await axios.patch(`/api/campaigns/${id}/toggle`, { isActive });
      message.success(`Campaign ${isActive ? "activated" : "deactivated"}.`);
      fetchCampaigns();
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    }
  };

  // Resolves to whether the update succeeded, so the edit modal stays open with the user's edits on failure.
  const updateHandler = async (id, data) => {
    try {
      const payload = {
        name: data.name,
        description: data.description || "",
        campaignDetails: normalizeCampaignDetails(data.campaignDetails),
        badge: {
          en: data.badge_en || "",
          zh: data.badge_zh || "",
          ms: data.badge_ms || "",
        },
        isFirstTopupOnly: data.isFirstTopupOnly,
        ...buildVoucherRewardPayload(data),
      };

      await axios.put(`/api/campaigns/${id}`, payload);

      message.success("Campaign updated.");
      fetchCampaigns();
      return true;
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
      return false;
    }
  };

  const setExporting = (id, isExporting) =>
    setLoading((prev) => {
      const exportingIds = new Set(prev.export);
      if (isExporting) exportingIds.add(id);
      else exportingIds.delete(id);
      return { ...prev, export: exportingIds };
    });

  const exportHandler = async (id) => {
    if (loading.export.has(id)) return;

    setExporting(id, true);
    try {
      const res = await axios.get(`/api/campaigns/${id}/export`);
      message.success(res.data.message);
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setExporting(id, false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Bonus Campaign"
        childRoutes={[
          { path: "/bonus-campaign", breadcrumbName: "Bonus Campaign" },
        ]}
      />

      <Space direction="vertical" size="middle" style={{ display: "flex" }}>
        {canCreate && (
          <Collapse
            expandIcon={({ isActive }) => (
              <CaretRightOutlined rotate={isActive ? 90 : 0} />
            )}
          >
            <Collapse.Panel header="Create New Campaign">
              <BonusCampaignForm
                form={form}
                loading={loading.create}
                mode="create"
                onSubmit={createHandler}
                onReset={() => form.resetFields()}
                submitText="Create Campaign"
                welcomePackageLabels={welcomePackageLabels}
                packageAmounts={buildPackageAmounts(
                  Object.keys(welcomePackageLabels)
                )}
                vouchers={vouchers}
              />
            </Collapse.Panel>
          </Collapse>
        )}

        <Collapse
          expandIcon={({ isActive }) => (
            <CaretRightOutlined rotate={isActive ? 90 : 0} />
          )}
          style={{ marginTop: 16 }}
        >
          <Collapse.Panel header="Filters" key="filters">
            <BonusCampaignFilters
              filters={filters}
              onFiltersChange={handleFiltersChange}
            />
          </Collapse.Panel>
        </Collapse>

        <BonusCampaignList
          campaigns={campaigns}
          loading={loading.index}
          canUpdate={canUpdate}
          onToggle={toggleHandler}
          onUpdate={updateHandler}
          canExport={canExport}
          exportingIds={loading.export}
          onExport={exportHandler}
          welcomePackageLabels={welcomePackageLabels}
          vouchers={vouchers}
        />

        <Row justify="end">
          <Col>
            <Pagination
              total={pagination?.totalSize}
              pageSize={pagination?.limit}
              current={pagination?.currentPage}
              onChange={(currentPage, limit) =>
                setPagination((prev) => ({ ...prev, currentPage, limit }))
              }
            />
          </Col>
        </Row>
      </Space>
    </div>
  );
};

export default BonusCampaign;
